<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-map-marker</v-icon>
        <v-toolbar-title>Ubicaciones</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-col cols="3" class="mr-1" v-if="$vuetify.breakpoint.mdAndUp">
          <v-text-field v-model="search" label="Buscar ubicación..." prepend-inner-icon="mdi-magnify"
            hide-details clearable rounded single-line filled dense dark></v-text-field>
        </v-col>
        <v-col cols="3" class="mr-3" v-if="$vuetify.breakpoint.mdAndUp">
          <v-select v-model="dealerFilter" :items="dealers" item-text="name" item-value="id" label="Filtrar por dealer"
            clearable hide-details rounded filled dense dark @change="load"></v-select>
        </v-col>
        <v-btn icon @click="importDialog = true" title="Importación masiva"><v-icon>mdi-file-upload</v-icon></v-btn>
        <v-btn icon @click="openForm(null)" title="Nueva ubicación"><v-icon>mdi-plus</v-icon></v-btn>
        <v-btn icon @click="load" title="Actualizar"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-toolbar>

      <v-data-table :headers="headers" :items="locations" :search="search" :loading="loading" item-key="id"
        :items-per-page="10" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }" no-data-text="No hay ubicaciones">
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
          <div class="text-caption grey--text" v-if="item.type">{{ item.type }}</div>
        </template>
        <template v-slot:item.address="{ item }">{{ item.address }}, {{ item.city }} ({{ item.zip }})</template>
        <template v-slot:item.dealers="{ item }">
          <v-chip v-for="d in (item.dealers || [])" :key="d.id" x-small class="mr-1 mb-1"
            :color="d.id === item.dealer_id ? 'primary' : undefined" :outlined="d.id === item.dealer_id">{{ d.name }}</v-chip>
          <v-chip x-small v-if="(item.dealers || []).length > 1" color="deep-purple" dark>Compartida</v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn icon small color="primary" @click="openForm(item)" title="Editar"><v-icon>mdi-pencil</v-icon></v-btn>
          <v-btn icon small color="error" @click="remove(item)" title="Eliminar"><v-icon>mdi-delete</v-icon></v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="640px" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-map-marker</v-icon>
          {{ editing ? 'Editar ubicación' : 'Nueva ubicación' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row dense>
              <v-col cols="8"><v-text-field v-model="form.name" label="Nombre *" outlined dense :rules="[required]"></v-text-field></v-col>
              <v-col cols="4"><v-text-field v-model="form.type" label="Tipo (campa, nave…)" outlined dense></v-text-field></v-col>
            </v-row>
            <v-text-field v-model="form.address" label="Dirección *" outlined dense :rules="[required]"></v-text-field>
            <v-row dense>
              <v-col cols="5"><v-text-field v-model="form.city" label="Población *" outlined dense :rules="[required]"></v-text-field></v-col>
              <v-col cols="4"><v-autocomplete v-model="form.state" :items="provincias" item-text="name" item-value="name" label="Provincia *" outlined dense auto-select-first no-data-text="Sin resultados" :rules="[required]"></v-autocomplete></v-col>
              <v-col cols="3"><v-text-field v-model="form.zip" label="CP *" outlined dense :rules="[required]"></v-text-field></v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6"><v-text-field v-model="form.phone" label="Teléfono" outlined dense></v-text-field></v-col>
              <v-col cols="6"><v-text-field v-model="form.email" label="Email" outlined dense></v-text-field></v-col>
            </v-row>
            <v-select v-model="form.dealer_id" :items="dealers" item-text="name" item-value="id" label="Dealer principal *"
              outlined dense :rules="[required]"></v-select>
            <v-autocomplete v-model="form.dealer_ids" :items="dealers.filter(d => d.id !== form.dealer_id)" item-text="name"
              item-value="id" label="Compartir también con" outlined dense multiple chips small-chips deletable-chips
              hint="En una ubicación compartida cada dealer solo ve sus propios vehículos (y los de su partner)."
              persistent-hint></v-autocomplete>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!valid" :loading="saving" @click="save">
            <v-icon left>mdi-content-save</v-icon>
            {{ editing ? 'Guardar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <import-dialog v-model="importDialog" title="Importación masiva de ubicaciones" template-name="plantilla_ubicaciones.xlsx"
      :template-fn="templateFn" :import-fn="importFn" @imported="load"></import-dialog>
  </v-container>
</template>

<script>
import LocationService from '@/services/LocationService'
import GrupoPanelService from '@/services/GrupoPanelService'
import ProvinceService from '@/services/ProvinceService'
import ImportDialog from '@/components/grupo/ImportDialog'
import { apiData, apiError } from '@/grupo'

const emptyForm = () => ({ name: '', type: '', address: '', city: '', state: '', zip: '', phone: '', email: '', dealer_id: null, dealer_ids: [] })

export default {
  name: 'GrupoUbicaciones',

  components: { ImportDialog },

  data: () => ({
    locations: [],
    dealers: [],
    provincias: [],
    dealerFilter: null,
    search: '',
    loading: false,
    dialog: false,
    importDialog: false,
    editing: null,
    valid: false,
    saving: false,
    form: emptyForm(),
    headers: [
      { text: 'Ubicación', value: 'name' },
      { text: 'Dirección', value: 'address' },
      { text: 'Dealers', value: 'dealers', sortable: false },
      { text: '', value: 'action', sortable: false, align: 'end' }
    ]
  }),

  mounted () {
    ProvinceService.getAll().then(list => { this.provincias = list.slice() }).catch(() => {})
    this.loadDealers()
    this.load()
  },

  methods: {
    required: v => !!v || 'Obligatorio',
    templateFn: () => GrupoPanelService.locationImportTemplate(),
    importFn: (file, dryRun) => GrupoPanelService.importLocations(file, dryRun),

    async loadDealers () {
      try {
        this.dealers = apiData(await GrupoPanelService.dealers()) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los concesionarios'))
      }
    },

    async load () {
      this.loading = true
      try {
        const params = { expand: 'dealers', 'per-page': 100 }
        if (this.dealerFilter) {
          params['filter[dealer_id]'] = this.dealerFilter
        }
        this.locations = apiData(await LocationService.get(params)) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar las ubicaciones'))
      } finally {
        this.loading = false
      }
    },

    openForm (location) {
      this.editing = location
      // Dato antiguo que no está en el catálogo: se muestra igualmente
      if (location && location.state && !this.provincias.some(p => p.name === location.state)) {
        this.provincias.push({ name: location.state })
      }
      this.form = location ? {
        name: location.name,
        type: location.type,
        address: location.address,
        city: location.city,
        state: location.state,
        zip: location.zip,
        phone: location.phone,
        email: location.email,
        dealer_id: location.dealer_id,
        dealer_ids: (location.dealers || []).map(d => d.id).filter(id => id !== location.dealer_id)
      } : emptyForm()
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },

    async save () {
      this.saving = true
      try {
        if (this.editing) {
          await LocationService.put({ id: this.editing.id, ...this.form })
          this.$toast.success('Ubicación actualizada')
        } else {
          await LocationService.post(this.form)
          this.$toast.success('Ubicación creada')
        }
        this.dialog = false
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo guardar la ubicación'))
      } finally {
        this.saving = false
      }
    },

    async remove (location) {
      const ok = await this.$confirm(`¿Eliminar la ubicación ${location.name}?`, { title: 'Eliminar ubicación' })
      if (!ok) return
      try {
        await LocationService.delete(location)
        this.load()
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo eliminar la ubicación'))
      }
    }
  }
}
</script>
